import { code as statuses } from 'statuses';

class NewUserContainer {
  constructor(
    $q,
    $location,
    $timeout,
    appErrorHandler,
    appEvents,
    appSessionCapabilities,
    permissions,
    uimessages,
    users,
    APP_EVENTS,
  ) {
    'ngInject';

    this.$q = $q;
    this.$location = $location;
    this.$timeout = $timeout;
    this.appErrorHandler = appErrorHandler;
    this.appEvents = appEvents;
    this.appSessionCapabilities = appSessionCapabilities;
    this.permissions = permissions;
    this.uimessages = uimessages;
    this.users = users;
    this.APP_EVENTS = APP_EVENTS;
    this.removeOpenSubscription = this.appEvents.on(
      this.APP_EVENTS.createUser.open,
      () => this.openModal(),
    );

    this.open = false;
    this.modalTitle = 'Create User';
    this.loading = false;
    this.error = '';
    this.initialName = '';
    this.initialEmail = '';
    this.scopes = [];
    this.showScopes = false;
    this.selectedScope = {};
  }
  $onDestroy() {
    this.removeOpenSubscription();
  }
  openModal(initialName = '', initialEmail = '', selectedScope = {}) {
    this.open = true;
    this.initialName = initialName;
    this.initialEmail = initialEmail;
    if (selectedScope) {
      this.selectedScope = selectedScope;
    }

    return this.appSessionCapabilities.isAdmin() ?
      this._loadScopes() :
      this.$q.resolve();
  }
  closeModal() {
    this.error = '';
    this.appEvents.emit(this.APP_EVENTS.createUser.close);
    this.open = false;
  }
  hideModal() {
    this.open = false;
  }
  changeScope(scope) {
    this.selectedScope = scope;
  }
  saveModal(name, email) {
    let result;
    if (this.loading) {
      result = this.$q.reject(new Error('The user is already being created'));
    } else {
      this.loading = true;
      this.error = '';
      const scopeId = this.selectedScope.id || '';
      result = this.users.new(name, email, scopeId)
      .then((user) => {
        this.appEvents.emit(this.APP_EVENTS.createUser.save, user);
        this.$location.path(`/users/${user.id}`).search('refresh', 'yes');
        this.closeModal();
      })
      .catch((error) => {
        let exists = false;
        if (error.status === statuses.conflict) {
          exists = true;
        } else if (error.message) {
          this.error = error.message;
        } else {
          this.appErrorHandler.handle(error, 'There was an error while creating the user');
        }

        return exists;
      })
      .then((exists) => {
        this.loading = false;
        return exists && this.handleExistingUser(name, email);
      });
    }

    return result;
  }
  askForActivation(name, email) {
    this.hideModal();
    return this.uimessages.confirm(
      'Reactivate User',
      `A user account with the email <strong>${email}</strong> already exists.`,
      {
        confirmText: 'Reactivate User',
      },
    )
    .then((response) => {
      this.$timeout(() => {
        this.openModal(name, email, null)
        .then(() => {
          if (response) {
            this.reactivateUser(email);
          }
        });
      });
    });
  }
  askForImport(name, email) {
    this.hideModal();
    return this.uimessages.confirm(
      'Provide Access',
      `A user account with the email <strong>${email}</strong> already exists, do you want to ` +
        'give it access to your accounts?',
      {
        confirmText: 'Provide Access',
      },
    )
    .then((response) => {
      this.$timeout(() => {
        this.openModal(name, email, null)
        .then(() => {
          if (response) {
            this.importUser(email);
          }
        });
      });
    });
  }
  handleExistingUser(name, email) {
    return this.appSessionCapabilities.isBrandAdmin(false) ?
      this.askForImport(name, email) :
      this.askForActivation(name, email);
  }
  reactivateUser(email) {
    let result;
    if (this.loading) {
      result = this.$q.reject(new Error('The user is already being reactivated'));
    } else {
      this.loading = true;
      this.error = '';
      result = this.users.reactivate(email)
      .then((user) => {
        this.appEvents.emit(this.APP_EVENTS.createUser.reactivate, user);
        this.$location.path(`/users/${user.id}`).search('refresh', 'yes');
        this.closeModal();
      })
      .catch((error) => {
        if (error.message) {
          this.error = error.message;
        } else {
          this.appErrorHandler.handle(error, 'There was an error while reactivating the user');
        }
      })
      .then(() => {
        this.loading = false;
      });
    }

    return result;
  }
  importUser(email) {
    let result;
    if (this.loading) {
      result = this.$q.reject(new Error('The user is already being imported'));
    } else {
      this.loading = true;
      this.error = '';
      result = this.users.import(email)
      .then((user) => {
        this.appEvents.emit(this.APP_EVENTS.createUser.import, user);
        this.$location.path(`/users/${user.id}`).search('refresh', 'yes');
        this.closeModal();
      })
      .catch((error) => {
        if (error.message) {
          this.error = error.message;
        } else {
          this.appErrorHandler.handle(error, 'There was an error while importing the user');
        }
      })
      .then(() => {
        this.loading = false;
      });
    }

    return result;
  }
  _loadScopes() {
    this.loading = true;
    const ignoredScopes = [
      'media_actions',
    ];
    return this.permissions.scopes()
    .then((scopes) => {
      this.scopes = scopes.filter((scope) => !ignoredScopes.includes(scope.name));
      this.showScopes = true;
    })
    .catch((error) => {
      this.appErrorHandler.silent(error);
    })
    .then(() => {
      this.loading = false;
    });
  }
}

export default {
  controller: NewUserContainer,
  template: `
    <modal-wrapper
      ng-if="$ctrl.open"
      modal-title="$ctrl.modalTitle"
      on-close="$ctrl.closeModal()"
    >
      <new-user-form
        loading="$ctrl.loading"
        error="$ctrl.error"
        initial-name="{{::$ctrl.initialName}}"
        initial-email="{{::$ctrl.initialEmail}}"
        scopes="$ctrl.scopes"
        selected-scope="$ctrl.selectedScope"
        show-scopes="$ctrl.showScopes"
        on-scope-change="$ctrl.changeScope(scope)"
        on-save="$ctrl.saveModal(name, email)"
        on-cancel="$ctrl.closeModal()"
      ></new-user-form>
    </modal-wrapper>
  `,
};
